/**
 * Global "/" keyboard shortcut for jumping to search, wired up across every
 * page by index.ts's initializeAll.
 *
 * Target priority (see task spec / AGENTS.md's search entry-points work):
 *   1. The compact search input in the sticky nav bar
 *      (components.templ's NavigationWithFeedURL,
 *      `[data-search-shortcut="nav-input"]`) - only when it is actually
 *      visible. It is hidden below the `sm` breakpoint (Tailwind/UnoCSS
 *      default: 640px, `hidden sm:flex`) in favor of an icon-only link, and
 *      is not rendered at all on /search/ (which has its own big form).
 *   2. The <proposal-search> Web Component's own input, on /search/ itself,
 *      via its public focusInput() method (proposal-search.ts) - it lives in
 *      shadow DOM, so it cannot be targeted by a plain selector.
 *
 * Visibility of the nav input is determined via matchMedia against the same
 * 640px breakpoint UnoCSS's `sm:` variant compiles to, mirroring how
 * index.ts's isTouchOnly() already uses matchMedia rather than reading
 * layout - jsdom (this project's test environment) has no real layout
 * engine, so a getBoundingClientRect/offsetParent check would be
 * untestable, while matchMedia is trivially mockable (see
 * search-shortcut.test.ts and index.test.ts's existing matchMedia mock).
 */

const NAV_INPUT_SELECTOR = '[data-search-shortcut="nav-input"]';
const PROPOSAL_SEARCH_SELECTOR = 'proposal-search';

/** Mirrors UnoCSS/presetUno's default `sm:` breakpoint (uno.config.mjs). */
const NAV_INPUT_VISIBLE_QUERY = '(min-width: 640px)';

/** A DOM element that offers a public focusInput() method (duck-typed, not imported, so this module never has to know about Lit or the component's internals). */
interface FocusableSearchElement extends Element {
  focusInput: () => void | Promise<void>;
}

function hasFocusInput(element: Element | null): element is FocusableSearchElement {
  return !!element && typeof (element as Partial<FocusableSearchElement>).focusInput === 'function';
}

function isNavInputVisible(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(NAV_INPUT_VISIBLE_QUERY).matches;
}

/**
 * Focuses the highest-priority available search input. Returns true if
 * something was focused (so the caller knows whether to preventDefault the
 * "/" keystroke), false if there is nothing to focus on this page.
 * Exported for testing.
 */
export function focusSearchInput(): boolean {
  const navInput = document.querySelector<HTMLInputElement>(NAV_INPUT_SELECTOR);
  if (navInput && isNavInputVisible()) {
    navInput.focus();
    return true;
  }

  const proposalSearch = document.querySelector(PROPOSAL_SEARCH_SELECTOR);
  if (hasFocusInput(proposalSearch)) {
    void proposalSearch.focusInput();
    return true;
  }

  return false;
}

/**
 * Reports whether event's real target (accounting for shadow DOM
 * retargeting via composedPath, since a keydown inside e.g.
 * <proposal-search>'s shadow input otherwise reports the host element as
 * both event.target and document.activeElement) is somewhere the "/" key
 * should be typed literally rather than treated as a shortcut.
 */
function isTypingTarget(event: KeyboardEvent): boolean {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  const target = (path[0] ?? event.target) as Element | null;
  if (!target || !(target instanceof Element)) return false;

  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;

  return target.closest('[contenteditable]:not([contenteditable="false"])') !== null;
}

/**
 * keydown handler for the global "/" shortcut. Ignores IME composition,
 * any modifier key (so e.g. "?" via Shift+/ is untouched), and keystrokes
 * that land in an editable target (including inside a Web Component's
 * shadow DOM - see isTypingTarget). Exported for testing.
 */
export function handleSearchShortcutKeydown(event: KeyboardEvent): void {
  if (event.defaultPrevented || event.isComposing) return;
  if (event.key !== '/') return;
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
  if (isTypingTarget(event)) return;

  if (focusSearchInput()) {
    event.preventDefault();
  }
}

/** Marks `document.documentElement` once the shortcut listener is bound, so re-running this init (e.g. multiple initializeAll calls in a test) never double-binds it. */
const INITIALIZED_ATTR = 'data-search-shortcut-initialized';

/**
 * Wires up the global "/" search shortcut. Safe to call more than once.
 */
export function initializeSearchShortcut(): void {
  if (document.documentElement.hasAttribute(INITIALIZED_ATTR)) return;
  document.documentElement.setAttribute(INITIALIZED_ATTR, 'true');

  document.addEventListener('keydown', handleSearchShortcutKeydown);
}
